import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [attempts, setAttempts] = useState(0);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus("error");
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus("error");
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        withCredentials: true
      });

      if (response.data.payment_status === "paid") {
        setStatus("success");
        return;
      } else if (response.data.status === "expired") {
        setStatus("error");
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error("Error checking payment status:", error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  const handleContinue = () => {
    // Redirect to Google Auth to ensure user is logged in
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <Card className="bg-zinc-900/40 border-white/5 p-8 max-w-md w-full text-center" data-testid="payment-status-card">
        {status === "loading" && (
          <>
            <Loader2 className="w-16 h-16 text-[#D4AF37] mx-auto mb-6 animate-spin" />
            <h1 className="text-2xl font-light text-white mb-3">Processing Payment</h1>
            <p className="text-zinc-400">Please wait while we confirm your payment...</p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-20 h-20 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-[#D4AF37]" />
            </div>
            <h1 className="text-2xl font-light text-white mb-3">Payment Successful!</h1>
            <p className="text-zinc-400 mb-8">
              Welcome to Prayer to Prototype. Your 7-day journey awaits.
            </p>
            <Button 
              onClick={handleContinue}
              className="btn-gold rounded-full px-8 py-6 gold-glow w-full"
              data-testid="continue-btn"
            >
              <span className="flex items-center justify-center gap-2">
                Begin Your Journey <ArrowRight className="w-5 h-5" />
              </span>
            </Button>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="text-2xl font-light text-white mb-3">Payment Issue</h1>
            <p className="text-zinc-400 mb-8">
              We couldn't confirm your payment. If you believe this is an error, please contact support.
            </p>
            <Button 
              onClick={() => navigate('/')}
              className="btn-outline-gold rounded-full px-8 w-full"
              data-testid="retry-btn"
            >
              Return to Home
            </Button>
          </>
        )}
      </Card>
    </div>
  );
};

export default PaymentSuccess;
